import { Box, Paper, Typography, useTheme } from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { CONTRAST_TEXT_COLORS, Project } from "vmd-types";

import { formatDateTime } from "../../utils/format-date";

export interface UpdatesViewProps {
  reportingDate: string;
  projects: Project[];
}

const getStatusCounts = (projects: Project[]) => {
  const total = projects.length;
  return {
    updated: projects.filter(
      (m) => m._calculated?.updateStatus?.status === "Updated"
    ).length,
    overdue: projects.filter(
      (m) => m._calculated?.updateStatus?.status === "Overdue"
    ).length,
    due: projects.filter(
      (m) => m._calculated?.updateStatus?.status === "Pending"
    ).length,
    total,
  };
};

export const UpdatesView: PimoReactComponent<UpdatesViewProps> = ({
  reportingDate,
  projects,
}) => {
  const theme = useTheme();

  const totals = getStatusCounts(projects);

  return (
    <>
      <Box sx={{ width: "100%", pt: 0 }}>
        <Box sx={{ px: 3, py: 3, pt: 0, mt: 1 }}>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{ color: "text.primary" }}
          >
            <strong>Next reporting: {formatDateTime(reportingDate)} </strong>
          </Typography>

          <Box sx={{ display: "flex", gap: 4, mb: 4, mt: 2 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                textAlign: "center",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 4,
                flex: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box component="img" src={"mood-sad.svg"} sx={{ pl: 1 }}></Box>
                <Typography sx={{ ml: 1, color: CONTRAST_TEXT_COLORS[2] }}>
                  Overdue
                </Typography>
              </Box>
              <Typography
                variant="h4"
                sx={{ mt: 2, color: theme.palette.primary.main }}
              >
                {totals.overdue}/{totals.total}
              </Typography>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                textAlign: "center",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 4,
                flex: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src={"mood-neutral.svg"}
                  sx={{ pl: 1 }}
                ></Box>
                <Typography sx={{ ml: 1, color: CONTRAST_TEXT_COLORS[1] }}>
                  Due
                </Typography>
              </Box>
              <Typography
                variant="h4"
                sx={{ mt: 2, color: theme.palette.primary.main }}
              >
                {totals.due}/{totals.total}
              </Typography>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                textAlign: "center",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 4,
                flex: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src={"mood-smile.svg"}
                  sx={{ pl: 1 }}
                ></Box>
                <Typography sx={{ ml: 1, color: CONTRAST_TEXT_COLORS[0] }}>
                  Updated
                </Typography>
              </Box>
              <Typography
                variant="h4"
                sx={{ mt: 2, color: theme.palette.primary.main }}
              >
                {totals.updated}/{totals.total}
              </Typography>
            </Paper>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mt: 4,
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ color: "text.primary" }}>
              Measure
            </Typography>
            <Typography variant="h6" sx={{ color: "text.primary" }}>
              Update Status
            </Typography>
          </Box>

          <Box>
            {projects.map((project) => (
              <Box
                key={project.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 2,
                  "&:hover": { bgcolor: "action.hover", borderRadius: 1 },
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ color: "text.primary" }}>
                    {project.customId} - {project.name}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "right" }}>
                  {project._calculated?.updateStatus?.status === "Updated" && (
                    <Box
                      component="img"
                      src={"mood-smile.svg"}
                      sx={{ pl: 1 }}
                    ></Box>
                  )}
                  {project._calculated?.updateStatus?.status === "Pending" && (
                    <Box
                      component="img"
                      src={"mood-neutral.svg"}
                      sx={{ pl: 1 }}
                    ></Box>
                  )}
                  {project._calculated?.updateStatus?.status === "Overdue" && (
                    <Box
                      component="img"
                      src={"mood-sad.svg"}
                      sx={{ pl: 1 }}
                    ></Box>
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      ml: 1,
                      color:
                        project._calculated?.updateStatus?.status === "Updated"
                          ? CONTRAST_TEXT_COLORS[0]
                          : project._calculated?.updateStatus?.status ===
                              "Pending"
                            ? CONTRAST_TEXT_COLORS[1]
                            : CONTRAST_TEXT_COLORS[2],
                      display: "flex",
                      alignItems: "center",
                      height: "24px",
                    }}
                  >
                    {project._calculated?.updateStatus?.status}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </>
  );
};
